import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { AgentV2ConversationsActions } from '@/agent-v2/conversations/store/slice'
import type { AppDispatch, RootState } from '@/store/store'
import getPaxtonSkills from '@/user-skills/fetch/get-paxton-skills'
import getUserSkills from '@/user-skills/fetch/get-user-skills'
import { UserSkillChatActions } from './chat.slice'
import { UserSkillLibraryActions } from './library.slice'

export function createUserSkillLibraryListenerMiddleware() {
  const userSkillLibraryListenerMiddleware = createListenerMiddleware()
  const startListener = userSkillLibraryListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

  startListener({
    matcher: isAnyOf(UserSkillLibraryActions.refreshLibrary, AgentV2ConversationsActions.nullifyData),
    effect: async (action, listenerApi) => {
      if (!UserSkillLibraryActions.refreshLibrary.match(action)) {
        listenerApi.cancelActiveListeners()
        return
      }
      const library = listenerApi.getState().userSkillLibraryState
      if (!action.payload.force && (library.loaded || library.loading)) return

      // A forced refresh supersedes an older fetch; logout also cancels its result delivery.
      listenerApi.cancelActiveListeners()
      listenerApi.dispatch(UserSkillLibraryActions.loadStarted())
      try {
        const [skills, paxtonSkills] = await Promise.all([getUserSkills(), getPaxtonSkills()])
        if (listenerApi.signal.aborted) return
        listenerApi.dispatch(UserSkillLibraryActions.loadSucceeded({ skills, paxtonSkills }))
      } catch {
        if (listenerApi.signal.aborted) return
        listenerApi.dispatch(UserSkillLibraryActions.loadFailed())
      }
    },
  })

  startListener({
    actionCreator: UserSkillChatActions.skillSaved,
    effect: (_action, listenerApi) => {
      listenerApi.dispatch(UserSkillLibraryActions.refreshLibrary({ force: true }))
    },
  })

  return userSkillLibraryListenerMiddleware
}
